import { Logger } from '@/utils/logging';

export interface StorageAdapter {
  getItem(key: string): string | null | undefined;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const DEFAULT_TTL_MS = 15 * 60 * 1000;

export function createPaymentReturnStorage<
  T extends { createdAt: number; cartId: string },
>(
  storageKey: string,
  validate: (data: unknown) => data is T,
  adapter: StorageAdapter,
  ttlMs = DEFAULT_TTL_MS,
) {
  return {
    get(cartId?: string): T | null {
      try {
        const raw = adapter.getItem(storageKey);
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        if (!validate(parsed)) return null;
        if (cartId && (parsed as T).cartId !== cartId) return null;
        if (Date.now() - (parsed as T).createdAt > ttlMs) {
          Logger.warn(`[${storageKey}] session expired`, { cartId });
          adapter.removeItem(storageKey);
          return null;
        }
        return parsed as T;
      } catch (error) {
        Logger.error(`[${storageKey}] failed to parse session storage`, {
          error,
        });
        return null;
      }
    },

    set(params: Omit<T, 'createdAt'>): void {
      adapter.setItem(
        storageKey,
        JSON.stringify({ ...params, createdAt: Date.now() }),
      );
    },

    clear(): void {
      adapter.removeItem(storageKey);
    },
  };
}
